import { useState } from "react";
import type { Palette } from "../api/types";
import { ThemeImportError, importTheme } from "../lib/themeShare";
import { ErrorMessage, Modal } from "./common";

/**
 * Where a theme someone exported gets pasted back in.
 *
 * This only parses the text — it doesn't save anything itself. A successful
 * parse hands the fields to ThemeEditor as a starting point, the same way
 * opening an existing palette does, so the person reviews and can still change
 * anything before it becomes one of their saved themes.
 */
export function ThemeImportModal({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: (theme: {
    name: string;
    description: string;
    palette: Palette;
  }) => void;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  function handleImport() {
    try {
      onImported(importTheme(text));
    } catch (err) {
      setError(
        err instanceof ThemeImportError
          ? err.message
          : "That doesn't look like an exported theme.",
      );
    }
  }

  return (
    <Modal title="Import a theme" onClose={onClose}>
      <div className="stack">
        <ErrorMessage>{error}</ErrorMessage>
        <p className="muted">
          Paste the text someone exported from their colors below.
        </p>
        <textarea
          className="theme-share__textarea"
          rows={10}
          value={text}
          autoFocus
          onChange={(e) => {
            setText(e.target.value);
            setError("");
          }}
          placeholder="Paste exported theme text here"
        />
        <div className="row row--between">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!text.trim()}
            onClick={handleImport}
          >
            Import
          </button>
        </div>
      </div>
    </Modal>
  );
}
